import { ApiError } from "../lib/api";
import type { Reglage } from "./bobines-requetes";
import {
  AGE_ALERTE_JOURS,
  type EtatBobine,
  PREAVIS_JETON_JOURS,
  type RaisonEchec,
  type ReferenceVue,
  type VueBobines,
} from "./contract";
import { compter } from "./format";

/**
 * Les textes et les petites regles d'affichage du module Bobines (#220) : durees, grammes,
 * etats en clair, raisons d'un echec de la synchro, liens de la boutique Bambu, filtre du
 * stock, les deux compteurs d'une ligne (minimum et reserve) et le choix d'une couleur a
 * mettre en reserve. Tout est pur ; l'instant courant est toujours passe en argument.
 */

/** Une bobine d'une reference, une gamme de la liste de courses et l'une de ses lignes. */
export type BobineVue = ReferenceVue["bobines"][number];
export type GroupeCourses = VueBobines["courses"][number];
export type LigneCourses = GroupeCourses["lignes"][number];

// ---------------------------------------------------------------------------------------
// Les commandes et le guide du PC, cites tels quels dans la page.

export const COMMANDE_CONNEXION = "./scripts/bobines.ps1 connexion";
export const COMMANDE_VERIFIER = "./scripts/bobines.ps1 verifier";
/**
 * Le guide de l'agent du PC (`docs/gestes/bobines-agent.md`, partie C du plan, PR #246) :
 * verifier, premiere synchro, minimums et reserve, tache planifiee. La carte du premier
 * lancement y mene, et l'encadre de l'agent muet aussi.
 */
export const GUIDE_AGENT = "bobines-agent";

// ---------------------------------------------------------------------------------------
// Durees et dates

const MINUTE = 60_000;
const HEURE = 60 * MINUTE;
export const JOUR = 24 * HEURE;

/** Une duree en clair, sans « il y a » : « 12 min », « 2 h », « 5 jours ». */
export function dureeEnClair(ms: number): string {
  if (ms < MINUTE) return "moins d'une minute";
  if (ms < HEURE) return `${Math.floor(ms / MINUTE)} min`;
  if (ms < JOUR) return `${Math.floor(ms / HEURE)} h`;
  return compter(Math.floor(ms / JOUR), "jour", "jours");
}

/** Le temps ecoule depuis un horodatage : « il y a 2 h », « à l'instant ». */
export function ilYA(horodatage: string, maintenant: number): string {
  const ecoule = maintenant - Date.parse(horodatage);
  if (!(ecoule >= MINUTE)) return "à l'instant";
  return `il y a ${dureeEnClair(ecoule)}`;
}

/** Depuis combien de temps : « 2 jours », « moins d'une minute ». */
export function depuis(horodatage: string, maintenant: number): string {
  return dureeEnClair(Math.max(0, maintenant - Date.parse(horodatage)));
}

/**
 * Au-dela de `AGE_ALERTE_JOURS`, ce que l'agent a envoye en dernier est trop vieux : la copie
 * (`vue_le`) ou son signe d'echec (`le`). L'agent ne tourne plus ?
 */
export function tropAncien(horodatage: string, maintenant: number): boolean {
  return maintenant - Date.parse(horodatage) > AGE_ALERTE_JOURS * JOUR;
}

const JOUR_EN_CLAIR = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "Europe/Paris",
});

/** « 3 octobre 2026 », a l'heure de Paris. */
export function jourEnClair(horodatage: string): string {
  return JOUR_EN_CLAIR.format(new Date(horodatage));
}

/**
 * Le preavis du jeton Bambu : rien tant qu'il vaut plus de `PREAVIS_JETON_JOURS` jours, sinon
 * sa date et s'il est deja perime. Le jeton ne se renouvelle pas seul (porte 0) : c'est a
 * Nicolas de relancer la connexion.
 */
export function preavisJeton(
  expireLe: string | null,
  maintenant: number,
): { readonly date: string; readonly expire: boolean } | null {
  if (expireLe === null) return null;
  const reste = Date.parse(expireLe) - maintenant;
  if (Number.isNaN(reste) || reste > PREAVIS_JETON_JOURS * JOUR) return null;
  return { date: jourEnClair(expireLe), expire: reste <= 0 };
}

// ---------------------------------------------------------------------------------------
// Les raisons d'un echec de la synchro, et ce qu'il faut faire

/**
 * Ce qu'il faut faire, en une phrase : un texte, et au besoin une commande du PC au milieu
 * (posee dans un `code` par la page), puis la fin de la phrase.
 */
export type Geste = {
  readonly avant: string;
  readonly commande: string | null;
  readonly apres: string;
};

/** Ce que dit le bandeau pour chaque raison que l'agent envoie (`raisonEchecSchema`). */
export const RAISONS: Readonly<Record<RaisonEchec, { quoi: string; geste: Geste }>> = {
  jeton_bambu_absent: {
    quoi: "l'agent du PC n'a pas de jeton Bambu.",
    geste: { avant: "Connecte-toi sur le PC :", commande: COMMANDE_CONNEXION, apres: "" },
  },
  jeton_bambu_refuse: {
    quoi: "ton jeton Bambu a expiré ou a été refusé.",
    geste: { avant: "Reconnecte-toi sur le PC :", commande: COMMANDE_CONNEXION, apres: "" },
  },
  api_bambu_refuse: {
    quoi: "Bambu refuse la lecture de l'inventaire.",
    geste: {
      avant:
        "L'agent réessaie toutes les heures. Si ça dure, préviens Claude : l'agent est peut-être à adapter.",
      commande: null,
      apres: "",
    },
  },
  api_bambu_format: {
    quoi: "l'inventaire de Bambu n'a plus le format attendu.",
    geste: {
      avant: "Rien à faire de ton côté : préviens Claude, l'agent est à adapter.",
      commande: null,
      apres: "",
    },
  },
  reseau_bambu: {
    quoi: "le PC n'arrive pas à joindre le cloud Bambu.",
    geste: {
      avant: "L'agent réessaie toutes les heures. Si ça dure, sur le PC,",
      commande: COMMANDE_VERIFIER,
      apres: "dit ce qui coince.",
    },
  },
  autre: {
    quoi: "l'agent du PC a rencontré une erreur imprévue.",
    geste: {
      avant: "Sur le PC,",
      commande: COMMANDE_VERIFIER,
      apres: "dit ce qui coince ; sinon, préviens Claude.",
    },
  },
};

// ---------------------------------------------------------------------------------------
// Grammes, etats, comptes

/** `1 710 g`, espaces insecables : le nombre et son unite ne se separent jamais. */
export function grammes(valeur: number): string {
  const entier = String(Math.round(valeur)).replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0");
  return `${entier}\u00a0g`;
}

const ETATS: Readonly<Record<EtatBobine, readonly [string, string]>> = {
  neuve: ["neuve", "neuves"],
  entamee: ["entamée", "entamées"],
  presque_vide: ["presque vide", "presque vides"],
  finie: ["finie", "finies"],
};

export function etatEnClair(etat: EtatBobine): string {
  return ETATS[etat][0];
}

/**
 * Une couleur sans bobine dans le Filament Manager mais avec de la reserve : ses bobines sont
 * sur l'etagere, jamais passees par l'AMS.
 */
export const EN_RESERVE_SEULEMENT = "en réserve seulement";

/**
 * « 1 neuve, 1 entamée, 1 presque vide », dans l'ordre des etats, sans les zeros. Sans bobine :
 * en reserve seulement, ou aucune bobine du tout.
 */
export function etatsEnClair(
  reference: Pick<ReferenceVue, "etats" | "bobines" | "reserve">,
): string {
  if (reference.bobines.length === 0) {
    return reference.reserve > 0 ? EN_RESERVE_SEULEMENT : "aucune bobine dans le Filament Manager";
  }
  return (Object.keys(ETATS) as EtatBobine[])
    .filter((etat) => reference.etats[etat] > 0)
    .map((etat) => {
      const [un, plusieurs] = ETATS[etat];
      return compter(reference.etats[etat], un, plusieurs);
    })
    .join(", ");
}

/**
 * La version courte du telephone (maquette `BobinesMobile.dc.html`) : les grammes, puis
 * seulement ce qui demande un regard, presque vides et finies.
 */
export function etatsCourts(
  reference: Pick<ReferenceVue, "etats" | "bobines" | "restant_g" | "reserve">,
): string {
  if (reference.bobines.length === 0) {
    return reference.reserve > 0 ? EN_RESERVE_SEULEMENT : "aucune bobine";
  }
  const notes = (["presque_vide", "finie"] as const)
    .filter((etat) => reference.etats[etat] > 0)
    .map((etat) => compter(reference.etats[etat], ETATS[etat][0], ETATS[etat][1]));
  return [grammes(reference.restant_g), ...notes].join(", ");
}

/** « 2 en réserve » : le mot ne s'accorde pas. */
export function enReserve(nombre: number): string {
  return `${nombre} en réserve`;
}

/**
 * Ce que dit une ligne de courses, les neuves et la reserve face au minimum : « 1 neuve + 1 en
 * réserve, minimum 3 », « 1 en réserve seulement, minimum 2 » (sans bobine dans l'AMS),
 * « plus aucune bobine, minimum 1 ».
 */
export function pourquoiEnClair(
  ligne: Pick<LigneCourses, "neuves" | "reserve" | "minimum" | "sans_bobine">,
): string {
  const minimum = `minimum ${ligne.minimum}`;
  if (ligne.sans_bobine) {
    return ligne.reserve > 0
      ? `${enReserve(ligne.reserve)} seulement, ${minimum}`
      : `plus aucune bobine, ${minimum}`;
  }
  const comptes = [
    ...(ligne.neuves > 0 ? [compter(ligne.neuves, "neuve", "neuves")] : []),
    ...(ligne.reserve > 0 ? [enReserve(ligne.reserve)] : []),
  ];
  return `${comptes.length === 0 ? "aucune neuve" : comptes.join(" + ")}, ${minimum}`;
}

export function aRacheterEnClair(nombre: number): string {
  return `${nombre} à racheter`;
}

/** La part d'une jauge : 0 pour une finie, 4 % au moins pour ce qui reste, 100 % au plus. */
export function remplissage(bobine: Pick<BobineVue, "restant_g" | "depart_g">): number {
  if (bobine.restant_g <= 0) return 0;
  const plein =
    bobine.depart_g !== null && bobine.depart_g > 0
      ? bobine.depart_g
      : Math.max(1000, bobine.restant_g);
  return Math.min(100, Math.max(4, Math.round((bobine.restant_g / plein) * 100)));
}

/** Une couleur de filament en CSS, seulement si elle a la forme attendue. */
export function couleurCss(hex: string): string {
  return /^[0-9a-f]{6}$/i.test(hex) ? `#${hex}` : "transparent";
}

// ---------------------------------------------------------------------------------------
// Marques, gammes et boutique Bambu

/** Minuscules, sans accents, sans espaces, `+` ni tirets : « PLA Silk+ » devient « plasilk ». */
export function normaliser(texte: string): string {
  return texte
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[\s+-]/g, "");
}

export function estBambu(marque: string): boolean {
  const nom = normaliser(marque);
  return nom === "bambulab" || nom === "bambu";
}

/** Le titre d'une gamme : « PLA Basic » chez Bambu, « eSun PLA+ » chez les autres. */
export function titreGamme(groupe: Pick<GroupeCourses, "marque" | "gamme">): string {
  return estBambu(groupe.marque) ? groupe.gamme : `${groupe.marque} ${groupe.gamme}`;
}

/**
 * Le nom d'une reference dans les noms accessibles : « Black, PLA Basic ». La couleur seule ne
 * suffit pas, « Black » existe dans plusieurs gammes, et deux boutons de la page ne portent
 * jamais le meme nom.
 */
export function nomComplet(
  reference: Pick<ReferenceVue, "couleur_nom" | "marque" | "gamme">,
): string {
  return `${reference.couleur_nom}, ${titreGamme(reference)}`;
}

export const BOUTIQUE_BAMBU = "https://eu.store.bambulab.com/fr/products/";

/**
 * Les fiches de gamme de la boutique Bambu Europe, verifiees le 27 septembre 2026 (HTTP 200),
 * par nom de gamme tel que Bambu l'ecrit. La porte 0 (28 septembre 2026) l'a confirme : l'agent
 * envoie les noms de gamme de Bambu Studio, « PLA Basic » et « PLA Matte » pour les bobines de
 * Nicolas, « PETG HF », « PLA Silk+ » ou « TPU for AMS » dans le catalogue. Une gamme se
 * rapproche par `normaliser`, et une ligne de plus suffit a en ajouter une. Une gamme absente
 * d'ici n'a pas de lien.
 */
const FICHES_BAMBU: readonly (readonly [gamme: string, fiche: string])[] = [
  ["PLA Basic", "pla-basic-filament"],
  ["PLA Matte", "pla-matte"],
  ["PETG HF", "petg-hf"],
  ["PLA Silk+", "pla-silk-upgrade"],
  ["PLA Silk Multi-Color", "pla-silk-multi-color"],
  ["PLA Translucent", "pla-translucent"],
  ["PETG Translucent", "petg-translucent"],
  ["ABS", "abs-filament"],
  ["ASA", "asa-filament"],
  ["TPU for AMS", "tpu-for-ams"],
  ["PLA Tough+", "pla-tough-upgrade"],
  ["PLA Metal", "pla-metal"],
  ["PLA Galaxy", "pla-galaxy"],
  ["PLA Sparkle", "pla-sparkle"],
  ["PLA Marble", "pla-marble"],
  ["PLA Wood", "pla-wood"],
  ["PLA Glow", "pla-glow"],
  ["PLA Basic Gradient", "pla-basic-gradient"],
  ["PLA Aero", "pla-aero"],
  ["PETG Basic", "petg-basic"],
  ["PLA-CF", "pla-cf"],
];

const FICHE_PAR_GAMME = new Map(FICHES_BAMBU.map(([gamme, fiche]) => [normaliser(gamme), fiche]));

/** Le lien de la gamme sur la boutique Bambu, ou null : autre marque, ou gamme inconnue. */
export function lienBoutique(marque: string, gamme: string): string | null {
  if (!estBambu(marque)) return null;
  const fiche = FICHE_PAR_GAMME.get(normaliser(gamme));
  return fiche === undefined ? null : `${BOUTIQUE_BAMBU}${fiche}`;
}

// ---------------------------------------------------------------------------------------
// Le filtre du stock

/**
 * Depuis la porte 0, les noms de couleur arrivent en francais (« Noir », « Blanc de jade ») :
 * l'agent les prend au catalogue de Bambu Studio. Mais une couleur que Bambu ne nomme qu'en
 * anglais garde son nom anglais : un mot francais du filtre cherche donc aussi son equivalent,
 * pour que « noir » trouve encore « Black ». Une `Map` et non un objet : un mot tape comme
 * « constructor » ou « __proto__ » lirait le prototype d'un objet, et la page tombait.
 */
const SYNONYMES: ReadonlyMap<string, readonly string[]> = new Map([
  ["noir", ["black"]],
  ["blanc", ["white"]],
  ["gris", ["gray", "grey"]],
  ["rouge", ["red"]],
  ["bleu", ["blue"]],
  ["vert", ["green"]],
  ["jaune", ["yellow"]],
  ["violet", ["purple"]],
  ["rose", ["pink"]],
  ["marron", ["brown"]],
  ["brun", ["brown"]],
  ["argent", ["silver"]],
  ["or", ["gold"]],
  ["ivoire", ["ivory"]],
  ["transparent", ["clear", "translucent"]],
]);

/** Vrai quand chaque mot du filtre est dans la marque, la gamme ou la couleur. */
export function correspond(
  reference: Pick<ReferenceVue, "marque" | "gamme" | "couleur_nom">,
  filtre: string,
): boolean {
  const champs = [reference.marque, reference.gamme, reference.couleur_nom].map(normaliser);
  return filtre
    .split(/\s+/)
    .map(normaliser)
    .filter((mot) => mot !== "")
    .every((mot) =>
      [mot, ...(SYNONYMES.get(mot) ?? [])].some((cherche) =>
        champs.some((champ) => champ.includes(cherche)),
      ),
    );
}

// ---------------------------------------------------------------------------------------
// Les deux compteurs d'une ligne : le minimum et la reserve

/**
 * Ce que dit chaque compteur. `etiquette` s'affiche avec lui quand la ligne est etroite (sur la
 * carte large, l'en-tete des colonnes le dit) ; les noms accessibles portent le nom complet de la
 * couleur (`nomComplet`, « Black, PLA Basic ») : deux boutons de la page ne portent jamais le
 * meme nom.
 */
export const COMPTEURS: Readonly<
  Record<
    Reglage,
    {
      readonly etiquette: string;
      readonly groupe: (nom: string) => string;
      readonly baisser: (nom: string) => string;
      readonly monter: (nom: string) => string;
    }
  >
> = {
  minimum: {
    etiquette: "Minimum",
    groupe: (nom) => `Minimum de ${nom}`,
    baisser: (nom) => `Baisser le minimum de ${nom}`,
    monter: (nom) => `Monter le minimum de ${nom}`,
  },
  reserve: {
    etiquette: "En réserve",
    groupe: (nom) => `Bobines en réserve de ${nom}`,
    baisser: (nom) => `Baisser la réserve de ${nom}`,
    monter: (nom) => `Monter la réserve de ${nom}`,
  },
};

/**
 * La legende de la reserve, sur la carte du stock. Elle dit la regle du decompte telle qu'elle
 * est (`baisseLaReserve` du backend) : toute bobine de la couleur vue pour la premiere fois,
 * sauf presque vide ou finie, pas seulement une neuve.
 */
export const LEGENDE_RESERVE =
  "En réserve : tes bobines neuves rangées hors de l'AMS. La réserve baisse toute seule quand une bobine de cette couleur apparaît pour la première fois dans l'AMS, sauf si elle est déjà presque vide ; une vieille bobine entamée, chargée pour la première fois, la fait baisser aussi.";

// ---------------------------------------------------------------------------------------
// Le refus d'un reglage

/**
 * La reserve avait change dans le dos de la page (409 `reserve_changee`) : une copie l'a fait
 * baisser entre la lecture de la page et le clic. La vue vient d'etre relue ; rien n'a ete
 * ecrit, le geste est a refaire depuis la valeur a jour.
 */
function reserveChangee(erreur: unknown): boolean {
  return erreur instanceof ApiError && erreur.status === 409;
}

/** Pourquoi un reglage n'est pas passe, en quelques mots. */
function raisonDuRefus(erreur: unknown, quoi: Reglage): string {
  const statut = erreur instanceof ApiError ? erreur.status : null;
  if (statut === 0 || statut === null) return "la console n'a pas répondu";
  if (statut === 401) return "ta session a expiré";
  if (statut === 403) return "la console a refusé l'écriture";
  if (statut === 404) {
    return quoi === "minimum"
      ? "cette couleur n'est plus dans le stock lu"
      : "la console ne connaît plus cette couleur";
  }
  if (statut === 409 && quoi === "reserve") {
    return "elle venait de changer (une bobine neuve est sans doute arrivée dans l'AMS)";
  }
  if (statut === 400) return "la console n'a pas accepté cette valeur";
  return "la console est en panne";
}

/**
 * Pourquoi un compteur n'est pas passe, en clair, et la valeur qui revient (maquette
 * `BobinesEtats.dc.html`, etat 8). Une reserve qui avait change ne « revient » pas : la ligne
 * montre deja la valeur relue, et c'est le geste qu'il faut refaire.
 */
export function refusEnClair(quoi: Reglage, erreur: unknown, avant: number): string {
  const raison = raisonDuRefus(erreur, quoi);
  if (quoi === "minimum") return `Minimum pas enregistré : ${raison}. Il revient à ${avant}.`;
  return reserveChangee(erreur)
    ? `Réserve pas enregistrée : ${raison}. Refais ton geste.`
    : `Réserve pas enregistrée : ${raison}. Elle revient à ${avant}.`;
}

// ---------------------------------------------------------------------------------------
// Ajouter une couleur en reserve, depuis le catalogue de Bambu Studio

export const AJOUT_RESERVE = {
  ouvrir: "Ajouter une couleur en réserve",
  consigne: "Choisis la gamme, puis la couleur : sa réserve monte d'une bobine à chaque fois.",
  gamme: "Gamme",
  choisirGamme: "Choisis une gamme",
  lecture: "Lecture du catalogue...",
  sansCatalogue:
    "Le catalogue des couleurs de Bambu Studio n'est pas encore arrivé : il arrive avec la prochaine synchro de l'agent du PC.",
  panne: "La console n'a pas pu lire le catalogue des couleurs.",
  reessayer: "Réessayer",
  fermer: "Fermer",
} as const;

/** Le nom de la liste des couleurs d'une gamme : « Couleurs de PLA Basic ». */
export function couleursDe(titre: string): string {
  return `Couleurs de ${titre}`;
}

/** Le nom accessible d'une couleur du catalogue : son nom visible d'abord (WCAG 2.5.3). */
export function ajouterEnReserve(nom: string): string {
  return `${nom} : une bobine de plus en réserve`;
}

/**
 * Ce que le choix dit d'une couleur qui a deja sa ligne dans le stock : « déjà dans le stock »,
 * et sa reserve quand elle en a. `null` pour une couleur que la page ne montre pas encore.
 */
export function dejaSuivie(reference: Pick<ReferenceVue, "reserve"> | undefined): string | null {
  if (reference === undefined) return null;
  return reference.reserve > 0
    ? `déjà dans le stock, ${enReserve(reference.reserve)}`
    : "déjà dans le stock";
}

/** Ce que le choix dit apres un ajout : « Noir, PLA Basic : 2 en réserve. » */
export function ajoutFait(nom: string, reserve: number): string {
  return `${nom} : ${enReserve(reserve)}.`;
}

/** Pourquoi un ajout n'est pas passe, en clair ; le geste est a refaire si la reserve avait change. */
export function refusAjout(erreur: unknown): string {
  const raison = raisonDuRefus(erreur, "reserve");
  return reserveChangee(erreur)
    ? `Réserve pas enregistrée : ${raison}. Refais ton geste.`
    : `Réserve pas enregistrée : ${raison}.`;
}
